<template>
  <!-- Table Container Card -->
  <b-card>
    <div class="m-2">
      <!-- Table Top -->
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <Button
            v-if="getUserPermission('CREATE_SAVING_CURRENT_ACCOUNT')"
            :label="$t('Add')"
            size="md"
            @click="gotoAddCustomerAccount"
          />
        </b-col>

        <!-- Search -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="casaAccountTableRef"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      :items="customerAccountList"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >
      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>
      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(dateCreated)="data">
        {{ toDateWithYear(data.value) }}
      </template>

      <template #cell(accountStatus)="data">
        <b-badge
          pill
          :variant="getStatusColor(data.value)"
        >
          {{ data.value }}
        </b-badge>
      </template>

      <!-- Column: Actions -->
      <template #cell(actions)="data">
        <div class="text-nowrap">
          <!-- Dropdown -->
          <b-dropdown
            variant="link"
            toggle-class="p-0"
            no-caret
            :right="$store.state.appConfig.isRTL"
            menu-class="custom-scroll"
          >
            <template #button-content>
              <feather-icon
                icon="MoreVerticalIcon"
                size="16"
                class="align-middle text-body"
              />
            </template>

            <b-dropdown-item
              v-if="getUserPermission('MODIFY_SAVING_CURRENT_ACCOUNT')"
              @click="onSelectCustomerAccountItem(data.item, $event.target, 'edit')"
            >
              <feather-icon icon="EditIcon" />
              <span class="align-middle ml-50">
                {{ $t('Modify') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item @click="onSelectCustomerAccountItem(data.item, $event.target, 'view')">
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">
                {{ $t('View') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission('VIEW_CUSTOMER_ACCOUNT_BALANCE')"
              @click="getCASABalance(data.item, $event.target)"
            >
              <feather-icon icon="EyeIcon" />
              <span class="align-middle ml-50">{{ $t('View Balance') }} </span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="
                getUserPermission(data.item.customerType.toLowerCase() !== 'individual' && 'CREATE_VIRTUAL_ACCOUNT')
              "
              @click="onSelectVirtualAccount(data.item, $event.target, 'create')"
            >
              <feather-icon icon="CloudIcon" />
              <span class="align-middle ml-50">
                {{ $t('Virtual Account') }}
              </span>
            </b-dropdown-item>

            <b-dropdown-item @click="customerAccountStatus(data.item, $event.target)">
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.accountStatus.toLowerCase() === 'active' ? $t('Deactivate') : $t('Activate')
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.accountStatus.toLowerCase() === 'pnd' ? 'UNPLACE_PND' : 'PLACE_PND')"
              @click="
                handleAccountStatus(
                  data.item,
                  data.item.accountStatus.toLowerCase() === 'pnd' ? 'RemovePND' : 'PlacePND'
                )
              "
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.accountStatus.toLowerCase() === 'pnd' ? $t('Remove PND') : $t('Place PND')
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="getUserPermission(data.item.accountStatus.toLowerCase() === 'pnc' ? 'UNPLACE_PNC' : 'PLACE_PNC')"
              @click="
                handleAccountStatus(
                  data.item,
                  data.item.accountStatus.toLowerCase() === 'pnc' ? 'RemovePNC' : 'PlacePNC'
                )
              "
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.accountStatus.toLowerCase() === 'pnc' ? $t('Remove PNC') : $t('Place PNC')
              }}</span>
            </b-dropdown-item>

            <b-dropdown-item
              v-if="
                getUserPermission(
                  data.item.accountStatus.toLowerCase() === 'frozen' ? 'UNFREEZE_ACCOUNT' : 'FREEZE_ACCOUNT'
                )
              "
              @click="
                handleAccountStatus(
                  data.item,
                  data.item.accountStatus.toLowerCase() === 'frozen' ? 'Unfreeze' : 'Freeze'
                )
              "
            >
              <feather-icon icon="CheckSquareIcon" />
              <span class="align-middle ml-50">{{
                data.item.accountStatus.toLowerCase() === 'frozen' ? $t('Unfreeze') : $t('Freeze')
              }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_ACCOUNT_LIMIT')"
              @click="manageAccountLimit(data.item, 'transfer')"
            >
              <feather-icon icon="ListIcon" />
              <span class="align-middle ml-50">{{ $t('Transfer Limit') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="getUserPermission('VIEW_CUSTOMER_USSD_LIMIT')"
              @click="manageAccountLimit(data.item, 'ussd')"
            >
              <feather-icon icon="ListIcon" />
              <span class="align-middle ml-50">{{ $t('USSD Limit') }}</span>
            </b-dropdown-item>

            <b-dropdown-item @click="setCustomerAccountHistories(data.item.accountNumber, $event.target)">
              <feather-icon icon="ListIcon" />
              <span class="align-middle ml-50">{{ $t('Recent Transactions') }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </div>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!--View customer modal -->
    <b-modal
      id="modal-lg"
      ref="my-modal"
      :ok-title="$t('Cancel')"
      ok-only
      :cancel-title="$t('Cancel')"
      ok-variant="outline-secondary"
      centered
      size="xl"
      :title="$t('Customer Information')"
    >
      <b-tabs>
        <b-tab
          :title="$t('Account Information')"
          active
        >
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="customer-id"
                v-model="customer.customerID"
                :label="$t('Customer ID')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-name"
                v-model="customer.accountName"
                :label="$t('Account Name')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-type"
                v-model="customer.accountType"
                :label="$t('Account Type')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="branch"
                v-model="customer.branchCode"
                :label="$t('Branch')"
                :reduce="option => option.value"
                :options="branchOptions"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="product-type"
                v-model="customer.productCode"
                :label="$t('Product Type')"
                :reduce="option => option.value"
                :options="productTypeOptions"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-status"
                v-model="customer.accountStatus"
                :label="$t('Account Status')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="customer.accountOfficerID"
                :label="$t('Account Officer')"
                :reduce="option => option.value"
                :options="allAccountOfficerOptions"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="account-tier-level"
                v-model.number="customer.accountTierLevel"
                :label="$t('Account Tier Level')"
                :options="accountTierOptions"
                item="label"
                :reduce="option => option.value"
                readonly
              />
            </b-col>

            <b-col md="6">
              <SelectField
                id="access-level"
                v-model.number="customer.accessLevel"
                :label="$t('Access Level')"
                :reduce="option => option.value"
                :options="accessLevelOptions"
                item="label"
                readonly
              />
            </b-col>
            <!--  Sector Code -->
            <b-col md="6">
              <SelectField
                id="means-of-id"
                v-model="customer.sectorCode"
                :label="$t('Sector Code')"
                :options="sectorCodeOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                readonly
              />
            </b-col>
            <!--  Sector Code -->
            <!--  Category of Account -->
            <b-col md="6">
              <SelectField
                id="means-of-id"
                v-model="customer.categoryOfAccount"
                :label="$t('Category of Account')"
                :options="categoryOfAccountOptions"
                :reduce="option => option.value"
                item="label"
                :placeholder="$t('Select')"
                readonly
              />
            </b-col>
            <!--  Category of Account -->

            <b-col md="6">
              <SelectField
                id="delivery-mode"
                v-model="customer.statementDeliveryMode"
                :label="$t('Delivery Mode')"
                :reduce="option => option.value"
                :options="deliveryModeOptions"
                item="label"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="statement-delivery"
                v-model="customer.statementDeliveryFrequency"
                :label="$t('Frequency')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputNumberField
                id="minimum-balance"
                v-model.number="customer.minimumBalanceRequired"
                :label="$t('Minimum Balance Required')"
                readonly
              />
            </b-col>
            <b-col md="6">
              <SelectField
                id="currency-code"
                v-model="customer.currencyCode"
                :label="$t('Currency')"
                :reduce="option => option"
                :options="currencyCodeOptions"
                item="label"
                :placeholder="$t('Select currency')"
                readonly
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col md="6">
              <TextAreaField
                id="sec-phone-number"
                v-model="customer.notificationPhoneNumber"
                :label="$t('Notification Phone Number')"
                :placeholder="
                  $t('For multiple Phone Numbers, separate by comma (,)')
                "
                type="text"
                for="sec-phone-number"
                rows="2"
                readonly
              />
            </b-col>
            <b-col md="6">
              <TextAreaField
                id="sec-email"
                v-model="customer.notificationEmail"
                :label="$t('Notification Email')"
                :placeholder="
                  $t('For multiple Emails, separate by comma (,)')
                "
                type="text"
                rows="2"
                readonly
              />
            </b-col>
          </b-row>

          <b-row class="d-flex align-items-center">
            <!--  Reference Number -->
            <b-col md="6">
              <InputField
                id="reference-number"
                v-model="customer.referenceNumber"
                :label="$t('Reference Number')"
                :placeholder="$t('Enter reference number')"
                type="text"
                show-icon
                :on-click="onViewInfo"
                readonly
              />
            </b-col>
            <!--  IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle"
                v-model="customer.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                readonly
              />
            </b-col>
            <!--  IPPIS/Oracle ID -->
            <b-col md="6">
              <b-col
                md="10"
                class="mt-1"
              >
                <div class="d-flex justify-content-between align-items-start">
                  <h6 class="">
                    {{ $t('Notify via') }}
                  </h6>

                  <b-form-checkbox
                    v-model="customer.enableEmailNotification"
                    inline
                    disabled
                  >
                    {{ $t('E-Mail') }}
                  </b-form-checkbox>
                  <b-form-checkbox
                    v-model="customer.enableSMSNotification"
                    inline
                    disabled
                  >
                    {{ $t('SMS') }}
                  </b-form-checkbox>
                </div>
              </b-col>
            </b-col>
          </b-row>

          <b-row class="match-height">
            <b-col md="6">
              <SwitchButton
                v-model="customer.overrideProductApplicableInterest"
                :label="$t('Override Product Applicable Interest')"
                name="default-credit"
                class="mt-2"
                readonly
              />
              <div v-if="setDefaultApplicableInterest(customer.overrideProductApplicableInterest)">
                <b-row>
                  <b-col md="6">
                    <SelectField
                      id="credit-interest"
                      v-model="customer.applyCreditInterest"
                      :label="$t('Apply Credit Interest')"
                      :reduce="option => option.value"
                      :options="applyOptions"
                      :placeholder="$t('Select')"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <!-- Conditional -->
                  <b-col
                    v-if="isConditional"
                    md="6"
                  >
                    <SelectField
                      id="conditional"
                      v-model="interestConditionBasis"
                      :label="$t('Conditional')"
                      :reduce="option => option.value"
                      :options="conditionalOptions"
                      :placeholder="$t('Select')"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <b-col
                    v-if="isCreditInterest"
                    md="6"
                  >
                    <InputField
                      id="credit-interest-rate"
                      v-model.number="customer.interestRate"
                      :label="$t('Credit Interest Rate')"
                      type="number"
                      readonly
                    />
                  </b-col>
                </b-row>

                <div>
                  <b-form
                    v-if="isConditional"
                    ref="form"
                    class="repeater-form"
                  >
                    <!-- Row Loop -->
                    <b-row
                      v-for="item in customer.productInterests"
                      :id="item.interestConditionValue"
                      :key="item.interestConditionValue"
                      ref="productInterest"
                      class="mt-2 d-flex align-items-center"
                    >
                      <b-col
                        v-if="isInterestNoOfWithdrawal"
                        md="6"
                      >
                        <!-- No of Withdrawals(times) -->
                        <InputNumberField
                          id="number-of-withdrawal"
                          v-model.number="item.interestConditionValue"
                          :label="$t('No of Withdrawals')"
                          readonly
                        />
                      </b-col>

                      <b-col
                        v-if="!isInterestNoOfWithdrawal"
                        md="6"
                      >
                        <!-- Customer's Balance (Amount) -->
                        <InputNumberField
                          id="customer-balance"
                          v-model="item.interestConditionValue"
                          :label="$t('Customer\'s Balance (Amount)')"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Credit Interest Rate -->
                        <InputNumberField
                          id="credit-interest-rate"
                          v-model.number="item.interestRate"
                          :label="$t('Rate')"
                          readonly
                        />
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
              </div>
            </b-col>

            <b-col md="6">
              <SwitchButton
                v-model="customer.overrideProductApplicableFee"
                :label="$t('Override Product Applicable Fee')"
                name="cam-fee"
                class="mt-2"
                readonly
              />
              <div v-if="setProductApplicableFee(customer.overrideProductApplicableFee)">
                <b-row>
                  <b-col md="6">
                    <SelectField
                      id="apply-fee"
                      v-model="customer.applyFee"
                      :label="$t('Apply Fee')"
                      :options="applyOptions"
                      :placeholder="$t('Select')"
                      :reduce="option => option.value"
                      item="label"
                      readonly
                    />
                  </b-col>

                  <b-col
                    v-if="isConditionalFee"
                    md="6"
                  >
                    <SelectField
                      id="conditional"
                      v-model="feeConditionBasis"
                      :label="$t('Conditional')"
                      :options="conditionalOptions"
                      :placeholder="$t('Select')"
                      :reduce="option => option.value"
                      item="label"
                      readonly
                    />
                  </b-col>
                </b-row>

                <div
                  v-if="isConditionalFee"
                  class="mb-5"
                >
                  <b-form
                    ref="productFeeForm"
                    class="repeater-form"
                    @submit.prevent="repeatProductFees"
                  >
                    <!-- Row Loop -->
                    <b-row
                      v-for="item in customer.productFees"
                      :id="item.id"
                      :key="item.id"
                      ref="productFees"
                      class="align-items-center"
                    >
                      <b-col
                        v-if="isFeeNoOfWithdrawal"
                        md="6"
                      >
                        <!-- No of Withdrawals(times) -->
                        <InputNumberField
                          id="number-of-withdrawal"
                          v-model.number="item.feeConditionValue"
                          :label="$t('No of Withdrawal')"
                          readonly
                        />
                      </b-col>

                      <b-col
                        v-if="!isFeeNoOfWithdrawal"
                        md="6"
                      >
                        <!-- Customer's Balance (Amount) -->
                        <InputNumberField
                          id="customer-balance"
                          v-model.number="item.feeConditionValue"
                          :label="$t('Customer\'s Balance (Amount)')"
                          readonly
                        />
                      </b-col>
                      <!-- Conditional -->

                      <b-col md="6">
                        <!-- Flat Amount -->
                        <InputNumberField
                          id="flat-amt"
                          v-model.number="item.flatAmount"
                          :label="$t('Flat Amount')"
                          rules="required|positive"
                          :error-name="$t('Flat Amount')"
                          readonly
                        />
                      </b-col>
                      <b-col md="6">
                        <!-- Amount Per Transaction -->
                        <InputNumberField
                          id="amt-per-trans"
                          v-model.number="item.flatAmountPerTransaction"
                          :label="$t('Amount per Trans')"
                          readonly
                        />
                      </b-col>
                      <b-col md="6">
                        <!-- Percentage on Turnover -->
                        <InputField
                          id="percentage-on-turnover"
                          v-model.number="item.percentageOnTurnover"
                          :label="$t('% on Turnover')"
                          type="number"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Minimum Amount To Charge -->
                        <InputNumberField
                          id="min-amount-to-charge"
                          v-model.number="item.minimumAmount"
                          :label="$t('Minimum Amount')"
                          readonly
                        />
                      </b-col>

                      <b-col md="6">
                        <!-- Maximum Amount To Charge -->
                        <InputNumberField
                          id="maximum-amount-to-charge"
                          v-model.number="item.maximumAmount"
                          :label="$t('Maximum Amount')"
                          readonly
                        />
                      </b-col>
                    </b-row>
                  </b-form>
                </div>
                <b-row v-if="isApplyFee">
                  <b-col md="6">
                    <InputNumberField
                      id="flat-amount"
                      v-model="customer.flatAmount"
                      :label="$t('Flat Amount')"
                      :placeholder="$t('Enter flat amount')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="flat-amount-per-transaction"
                      v-model="customer.flatAmountPerTransaction"
                      :label="$t('Flat Amount Per Transaction')"
                      :placeholder="$t('Enter flat amount per transaction')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="percentage-on-turnover"
                      v-model="customer.percentageOnTurnover"
                      :label="$t('Percentage on Turnover')"
                      :placeholder="$t('Enter percentage on turnover')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="maximum-amount-to-charge"
                      v-model.number="customer.maximumAmount"
                      :label="$t('Maximum Amount to Charge')"
                      :placeholder="$t('Enter maximum amount to charge')"
                      type="number"
                      readonly
                    />
                  </b-col>

                  <b-col md="6">
                    <InputNumberField
                      id="minimum-amount-to-charge"
                      v-model.number="customer.minimumAmount"
                      :label="$t('Minimum Amount to Charge')"
                      :placeholder="$t('Enter minimum amount to charge')"
                      type="number"
                      readonly
                    />
                  </b-col>
                </b-row>
              </div>
            </b-col>
          </b-row>
        </b-tab>

        <b-tab :title="$t('Referees')">
          <b-row class="match-height">
            <!-- Referee 1 -->
            <b-col md="6">
              <InputField
                id="customer-id"
                v-model="refereeInformation.referee1"
                :label="$t('Referee 1')"
                type="text"
                readonly
              />
            </b-col>

            <!-- Referee 2 -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="refereeInformation.referee2"
                :label="$t('Referee 2')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>

          <!-- Account Introducer -->
          <h6 class="section-label mt-3 mb-2">
            {{ $t('Account Introducer') }}
          </h6>
          <b-row class="match-height">
            <b-col md="6">
              <InputField
                id="introdcer-type"
                v-model="customer.introducerType"
                :label="$t('Account Introducer Type')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="account-introducer"
                v-model="accountIntroducerList.customerName"
                :label="$t('Account Introducer')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
        </b-tab>
        <b-tab :title="$t('Group Connections')">
          <b-row class="match-height">
            <!-- Referee 1 -->
            <b-col md="6">
              <InputField
                id="group-connections-id"
                v-model="customer.groupConnectionID"
                :label="$t('Group Connections ID')"
                type="text"
                readonly
              />
            </b-col>

            <b-col md="6">
              <InputField
                id="group-connections-name"
                v-model="customer.groupConnectionName"
                :label="$t('Group Connections Name')"
                type="text"
                readonly
              />
            </b-col>
          </b-row>
        </b-tab>

        <b-tab
          v-if="groupMemberList && groupMemberList.length"
          :title="$t('Group Members')"
        >
          <b-table
            ref="groupMemberListTable"
            select-mode="single"
            :items="groupMemberList"
            responsive
            :fields="translatedGroupMembersHeader"
            :per-page="tablePageSize"
            :current-page="tableCurrentPage"
            primary-key="id"
            selectable
            show-empty
            :empty-text="$t('No data available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ groupMemberTableIndex(data.index) }}
            </template>
          </b-table>
        </b-tab>

        <b-tab :title="$t('Signatories')">
          <b-table
            ref="signatoryListTable"
            select-mode="single"
            :items="signatoryList"
            responsive
            :fields="translatedSignatoryListHeaders"
            primary-key="id"
            selectable
            show-empty
            :empty-text="$t('No data available')"
            :sort-by.sync="sortBy"
            :sort-desc.sync="sortDesc"
            class="position-relative"
          >
            <template #cell(SN)="data">
              {{ getOverallIndex(data.index) }}
            </template>

            <template #cell(passport)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.passport"
                    button
                    @click="onViewPassport(data.item.passport, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>

            <template #cell(signature)="data">
              <b-media vertical-align="center">
                <template #aside>
                  <b-avatar
                    size="32"
                    :src="data.item.signature"
                    button
                    @click="onViewSignature(data.item.signature, data.item.fullName)"
                  />
                </template>
              </b-media>
            </template>
          </b-table>
        </b-tab>

        <b-tab :title="$t('Mandate Information')">
          <div>
            <b-table
              ref="mandateListTable"
              select-mode="single"
              :items="mandateList"
              responsive
              :fields="translatedMandateListHeaders"
              primary-key="id"
              selectable
              show-empty
              :empty-text="$t('No data available')"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              class="position-relative"
            >
              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>
              <template #cell(isLimited)="data">
                {{ data.value === false ? 'Unlimited' : 'Limited' }}
              </template>
              <template #cell(amount)="data">
                {{ data.value === 0 ? 'Unlimited' : toAmount(data.value) }}
              </template>
            </b-table>
          </div>
        </b-tab>
        <b-tab :title="$t('Other Documents')">
          <!-- <b-row class="match-height"> -->
          <b-form>
            <b-row
              v-for="(item, index) in documentsFormData"
              :id="index"
              :key="index"
              class="mb-2 match-height"
            >
              <b-col md="6">
                <SelectField
                  id="document-type"
                  v-model="item.type"
                  :reduce="option => option.value"
                  :label="$t('Document Type')"
                  :placeholder="$t('Select')"
                  :options="formattedDocumentTypeOptions"
                  item="label"
                  readonly
                />
              </b-col>
              <b-col md="7">
                <label>{{ $t('Upload Documents') }}</label>

                <div class="upload-image">
                  <b-form-group>
                    <b-form-file
                      :placeholder="$t('Choose a file or drop it here...')"
                      drop-:placeholder="$t('Drop file here..."
                      accept="image/jpeg, image/png, image/jpg, application/pdf, .doc, .docx, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                      plain
                      disabled
                      @change="onDocumentsUpload(index, $event)"
                    />
                    <div
                      v-if="!item.url"
                      class="upload-instruction"
                    >
                      <h4>{{ $t('Upload Document File') }}</h4>
                      <p>{{ $t('Drag & drop file or choose from your device to upload') }}</p>
                      <p class="format">
                        {{ $t('Format accepted') }} (jpg, png, jpeg, docx, pdf)
                      </p>
                    </div>
                    <div v-else>
                      <div
                        v-if="
                          item.fileExtension === 'png' || item.fileExtension === 'jpeg' || item.fileExtension === 'jpg'
                        "
                      >
                        <b-img
                          :src="item.url"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div v-else-if="item.fileExtension === 'pdf'">
                        <b-img
                          :src="pdfIcon"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div v-else-if="item.fileExtension === 'vnd'">
                        <b-img
                          :src="wordIcon"
                          block
                          rounded
                          width="250px"
                          height="250px"
                        />
                      </div>
                      <div v-else>
                        <b-img src="" />
                      </div>
                      <div
                        v-if="item.url"
                        class="view-details"
                      >
                        <feather-icon
                          icon="EyeIcon"
                          size="20"
                          class="text-primary"
                          @click="viewDocumentFile(item.url, item.fileName, item.fileExtension)"
                        />
                      </div>
                    </div>
                  </b-form-group>
                </div>
              </b-col>
              <b-col
                v-if="item.url"
                md="3"
                class="mt-0"
              >
                <b-button
                  v-ripple.400="'rgba(255, 255, 255, 0.15)'"
                  variant="primary"
                  class="ml-0"
                  @click="handleDownload(item.url)"
                >
                  <feather-icon
                    icon="DownloadIcon"
                    class="mr-25"
                  />
                  <span>{{ $t('Download') }}</span>
                </b-button>
              </b-col>
            </b-row>
          </b-form>
        </b-tab>
        <b-tab :title="$t('Memo')">
          <div>
            <b-table
              ref="memoListTable"
              select-mode="single"
              :items="memoGetData"
              responsive
              :fields="translatedMemoListHeaders"
              primary-key="id"
              selectable
              show-empty
              :empty-text="$t('No data available')"
              :sort-by.sync="sortBy"
              :sort-desc.sync="sortDesc"
              class="position-relative"
            >
              <template #cell(SN)="data">
                {{ getOverallIndex(data.index) }}
              </template>
            </b-table>
          </div>
        </b-tab>
      </b-tabs>
    </b-modal>
    <!--View customer modal -->

    <!-- View CASA Balance modal -->
    <b-modal
      id="casa-balance-modal"
      ref="casa-balance-modal"
      :ok-title="$t('Cancel')"
      ok-only
      ok-variant="outline-secondary"
      centered
      size="lg"
      :title="$t('CASA Balance')"
      modal-footer
    >
      <b-form>
        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Account Summary') }}
        </h5>
        <b-row class="match-height">
          <!-- Account Name -->
          <b-col md="6">
            <InputField
              id="account-name"
              v-model="casaBalanceFormData.accountName"
              :label="$t('Account Name')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Account Name -->

          <!-- Product Name -->
          <b-col md="6">
            <InputField
              id="product-name"
              v-model="casaBalanceFormData.productName"
              :label="$t('Product Name')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Product Name -->

          <!-- Account Number -->
          <b-col md="6">
            <InputField
              id="account-number"
              v-model="casaBalanceFormData.accountNumber"
              :label="$t('Account Number')"
              type="number"
              readonly
            />
          </b-col>
          <!--  Account Number -->

          <!-- Account Status -->
          <b-col md="6">
            <InputField
              id="account-status"
              v-model="casaBalanceFormData.accountStatus"
              :label="$t('Account Status')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Account Status -->

          <!-- Account Officer -->
          <b-col md="6">
            <InputField
              id="account-officer"
              v-model="casaBalanceFormData.accountOfficerName"
              :label="$t('Account Officer')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Account Officer -->

          <!--Branch-->
          <b-col md="6">
            <InputField
              id="branch"
              v-model.number="casaBalanceFormData.branchName"
              :label="$t('Branch')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Branch -->

          <!--Uncleared Balance-->
          <b-col md="6">
            <InputField
              id="uncleared-balance"
              v-model.number="casaBalanceFormData.unclearedEffect"
              :label="$t('Uncleared Balance')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Uncleared Balance -->

          <!-- Ledger Balance -->
          <b-col md="6">
            <InputField
              id="ledger-balance"
              v-model="casaBalanceFormData.ledgerBalance"
              :label="$t('Ledger Balance')"
              type="text"
              for="ledger-balance"
              readonly
            />
          </b-col>
          <!-- Ledger Balance -->

          <!-- Available Balance -->
          <b-col md="6">
            <InputField
              id="available-balance"
              v-model="casaBalanceFormData.availableBalance"
              :label="$t('Available Balance')"
              type="text"
              for="available-balance"
              readonly
            />
          </b-col>
          <!-- Available Balance -->

          <!-- Withdrawal Balance -->
          <b-col md="6">
            <InputField
              id="overdraft-limit"
              v-model="casaBalanceFormData.withdrawableBalance"
              :label="$t('Withdrawal Balance')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Withdrawal Balance -->

          <!--Active Overdraft Limit-->
          <b-col md="6">
            <InputField
              id="overdraft-limit"
              v-model.number="casaBalanceFormData.overdraftLimit"
              :label="$t('Active Overdraft Limit')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Active Overdraft Limit -->
        </b-row>

        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Outstanding Overdraft on CASA') }}
        </h5>
        <div class="d-flex font-weight-bold">
          <p class="h5">
            {{ $t('Total Outstanding Overdraft:') }}
          </p>
          <p class="ml-2 h5">
            {{ casaBalanceFormData.totalOutstandingOverdraft }}
          </p>
        </div>
        <b-row class="match-height">
          <!-- Overdrawn Amount -->
          <b-col md="6">
            <InputField
              id="overdrawn-amount"
              v-model="casaBalanceFormData.overdrawnAmount"
              :label="$t('Overdrawn Amount')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Overdrawn Amount -->

          <!-- Unpaid Overdrawn Interest-->
          <b-col md="6">
            <InputField
              id="unpaid-overdrawn-interest"
              v-model="casaBalanceFormData.unpaidOverdraftInterest"
              :label="$t('Unpaid Overdrawn Interest')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Unpaid Overdrawn Interest -->

          <!-- Unpaid Account Opening Fee-->
          <b-col md="6">
            <InputField
              id="unpaid-account-opening-fee"
              v-model="casaBalanceFormData.unpaidAccountOpeningFee"
              :label="$t('Unpaid Account Opening Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Unpaid Account Opening Fee -->

          <!-- Unpaid Account Maintenance Fee -->
          <b-col md="6">
            <InputField
              id="unpaid-account-maintenance-fee"
              v-model="casaBalanceFormData.unpaidAccountMaintenanceFee"
              :label="$t('Unpaid Account Maintenance Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid Account Maintenance Fee -->

          <b-col md="6">
            <!-- Unpaid CAM Fee -->
            <InputField
              id="unpaid-CAM-fee"
              v-model="casaBalanceFormData.unpaidCOTFee"
              :label="$t('Unpaid CAM Fee')"
              type="text"
              readonly
            />
            <!-- Unpaid CAM Fee -->
          </b-col>

          <!-- Unpaid Overdraft Fee-->
          <b-col md="6">
            <InputField
              id="unpaid-overdraft-fee"
              v-model="casaBalanceFormData.unpaidOverdraftFee"
              :label="$t('Unpaid Overdraft Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid Overdraft Fee-->

          <!-- Unpaid SMS Fee -->
          <b-col md="6">
            <InputField
              id="unpaid-sms-fee"
              v-model="casaBalanceFormData.unpaidSMSFee"
              :label="$t('Unpaid SMS Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid SMS Fee -->
        </b-row>

        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Outstanding Loan Amounts') }}
        </h5>
        <div class="d-flex font-weight-bold">
          <p class="h5">
            {{ $t('Total Loan Debt:') }}
          </p>
          <p class="ml-2 h5">
            {{ casaBalanceFormData.totalOutstandingLoan }}
          </p>
        </div>
        <b-row>
          <!-- Unpaid Loan Principal -->
          <b-col md="6">
            <InputField
              id="unpaid-loan-principal"
              v-model="casaBalanceFormData.unpaidLoanPrincipal"
              :label="$t('Unpaid Loan Principal')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid Loan Principal -->

          <!--  Unpaid Loan Interest  -->
          <b-col md="6">
            <InputField
              id="unpaid-loan-interest"
              v-model="casaBalanceFormData.unpaidLoanInterest"
              :label="$t('Unpaid Loan Interest')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Unpaid Loan Interest -->

          <!--  Unpaid Loan Fees -->
          <b-col md="6">
            <InputField
              id="unpaid-loan-fees"
              v-model="casaBalanceFormData.unpaidLoanFee"
              :label="$t('Unpaid Loan Fees')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid Loan Fees -->

          <!-- Unpaid Loan Penalty -->
          <b-col md="6">
            <InputField
              id="unpaid-loan-penalty"
              v-model="casaBalanceFormData.unpaidLoanPenalty"
              :label="$t('Unpaid Loan Penalty')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Unpaid Loan Penalty -->
        </b-row>

        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Accrued Amounts') }}
        </h5>
        <div class="d-flex font-weight-bold">
          <p class="h5">
            {{ $t('Total Accrued Amount:') }}
          </p>
          <p class="ml-2 h5">
            {{ casaBalanceFormData.totalAccruedAmount }}
          </p>
        </div>
        <b-row>
          <!-- Accrued CAM Fee -->
          <b-col md="6">
            <InputField
              id="accrued-CAM-fee"
              v-model="casaBalanceFormData.accruedCOTFee"
              :label="$t('Accrued CAM Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Accrued CAM Fee -->

          <!--  Accrued SMS Fee  -->
          <b-col md="6">
            <InputField
              id="accrued-sms-fee"
              v-model="casaBalanceFormData.accruedSMSFee"
              :label="$t('Accrued SMS Fee')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Accrued SMS Fee -->
          <!--  Accrued Loan Interest  -->
          <b-col md="6">
            <InputField
              id="accrued-loan-interest"
              v-model="casaBalanceFormData.accruedLoanInterest"
              :label="$t('Accrued Loan Interest')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Accrued Loan Interest -->
          <!--  Accrued Overdraft Interest  -->
          <b-col md="6">
            <InputField
              id="accrued-overdraft-interest"
              v-model="casaBalanceFormData.accruedOverdraftInterest"
              :label="$t('Accrued Overdraft Interest')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Accrued Overdraft Interest -->
          <!--  Defaulting Loan Interest  -->
          <b-col md="6">
            <InputField
              id="defaulting-loan-interest"
              v-model="casaBalanceFormData.defaultingLoanInterest"
              :label="$t('Defaulting Loan Interest')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Defaulting Loan Interest -->
        </b-row>

        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Other Withheld Amounts') }}
        </h5>
        <div class="d-flex font-weight-bold">
          <p class="h5">
            {{ $t('Total Withheld Amount:') }}
          </p>
          <p class="ml-2 h5">
            {{ casaBalanceFormData.totalWithheldAmount }}
          </p>
        </div>
        <b-row>
          <!--  Security Deposit -->
          <b-col md="6">
            <InputField
              id="security-deposit"
              v-model="casaBalanceFormData.securityDeposit"
              :label="$t('Security Deposit')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Security Deposit -->

          <!--  Product Minimum Balance  -->
          <b-col md="6">
            <InputField
              id="product-minimum-balance"
              v-model="casaBalanceFormData.productMinimumBalance"
              :label="$t('Product Minimum Balance')"
              type="text"
              readonly
            />
          </b-col>
          <!--  Product Minimum Balance -->
        </b-row>

        <h5 class="text-capitalize mb-2 mt-2">
          {{ $t('Lien Amount') }}
        </h5>
        <div class="d-flex font-weight-bold">
          <p class="h5">
            {{ $t('Total Lien:') }}
          </p>
          <p class="ml-2 h5">
            {{ casaBalanceFormData.totalLien }}
          </p>
        </div>
        <b-row>
          <!--   Percentage Lien -->
          <b-col md="6">
            <InputField
              id="percentage-lien"
              v-model="casaBalanceFormData.percentageLien"
              :label="$t('Percentage Lien')"
              type="text"
              readonly
            />
          </b-col>
          <!--   Percentage Lien -->

          <!--  Flat Lien  -->
          <b-col md="6">
            <InputField
              id="flat-lien"
              v-model="casaBalanceFormData.flatLien"
              :label="$t('Flat Lien')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Flat Lien -->
          <!--  Repayment Lien  -->
          <b-col md="6">
            <InputField
              id="repayment-lien"
              v-model="casaBalanceFormData.repaymentLien"
              :label="$t('Repayment Tracker Lien')"
              type="text"
              readonly
            />
          </b-col>
          <!-- Repayment Lien -->
        </b-row>
      </b-form>
    </b-modal>

    <!-- Filter modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Filters')"
      modal-footer
      @ok="applyFilters"
      @cancel="resetFilters"
    >
      <b-row class="match-height">
        <!-- Account Name -->
        <b-col md="6">
          <InputField
            id="account-name"
            v-model="filter.accountName"
            :label="$t('Account Name')"
            :placeholder="$t('Enter account name')"
            type="text"
          />
        </b-col>
        <!-- Account Name -->

        <!-- Status -->
        <b-col md="6">
          <SelectField
            id="status"
            v-model="filter.accountStatus"
            :label="$t('Status')"
            :placeholder="$t('Select')"
            :options="accountStatusOptions"
            item="label"
            :reduce="option => option.value"
          />
        </b-col>
        <!-- Status -->

        <!-- Account Number -->
        <b-col md="6">
          <InputField
            id="account-number"
            v-model="filter.accountNumber"
            :label="$t('Account Number')"
            :placeholder="$t('Enter account number')"
            type="text"
          />
        </b-col>
        <!-- Account Number -->

        <!-- Account Type -->
        <b-col md="6">
          <SelectField
            id="gender"
            v-model="filter.accountType"
            :label="$t('Account Type')"
            :placeholder="$t('Select account type')"
            :options="accountTypeOptions"
            :reduce="option => option.value"
            item="label"
          />
        </b-col>
        <!-- Account Type -->

        <!-- Customer Type -->
        <b-col md="6">
          <SelectField
            id="customer-type"
            v-model="filter.customerType"
            :label="$t('Customer Type')"
            :options="customerTypeOptions"
            item="label"
            :reduce="option => option.value"
            :placeholder="$t('Select')"
          />
        </b-col>
        <!-- Customer Type -->
        <!-- customerID -->
        <b-col md="6">
          <InputField
            id="customer-id"
            v-model="filter.customerID"
            :label="$t('Customer ID')"
            :placeholder="$t('Enter customer ID')"
            type="number"
            for="customer-id"
          />
        </b-col>
        <!-- customerID -->

        <!-- Branch -->
        <b-col md="6">
          <SelectField
            id="branch"
            v-model="filter.branchCode"
            :label="$t('Branch')"
            :options="branchOptions"
            :reduce="option => option.value"
            item="label"
            :placeholder="$t('Select Branch')"
          />
        </b-col>
        <!--Branch -->
        <!-- Product -->
        <b-col md="6">
          <SelectField
            id="product"
            v-model="filter.productCode"
            :label="$t('Product')"
            :reduce="option => option.value"
            :options="productOptions"
            item="label"
            :placeholder="$t('Select product')"
            :readonly="isReadonly"
          />
        </b-col>
        <!-- Product -->

        <!--Currency  -->
        <b-col md="6">
          <SelectField
            id="currency-code"
            v-model="filter.currencyCode"
            :label="$t('Currency')"
            :reduce="option => option"
            :options="currencyCodeOptions"
            item="label"
            :placeholder="$t('Select currency')"
          />
        </b-col>
        <!--Currency  -->

        <!-- Account Officer  -->
        <b-col md="6">
          <SelectField
            id="account-officer"
            v-model="filter.accountOfficerID"
            :label="$t('Account Officer')"
            :reduce="option => option.value"
            :options="accountOfficerOptions"
            item="label"
            :placeholder="$t('Select account officer')"
          />
        </b-col>
        <!--Account Officer  -->
        <!-- Reference Number -->
        <b-col md="6">
          <InputField
            id="reference-number"
            v-model="filter.referenceNumber"
            :label="$t('Reference Number')"
            :placeholder="$t('Enter Reference Number')"
            type="text"
            for="reference-number"
          />
        </b-col>
        <!--Reference Number -->

        <!-- BVN -->
        <b-col md="6">
          <InputField
            id="bvn"
            v-model="filter.bvn"
            :label="$t('BVN')"
            :placeholder="$t('Enter BVN')"
            type="text"
            for="bvn"
          />
        </b-col>
        <!--BVN -->
        <!-- IPPIS/Oracle ID -->
        <b-col md="6">
          <InputField
            id="ippis-oracle-id"
            v-model="filter.employeeID"
            :label="$t('IPPIS/Oracle ID')"
            :placeholder="$t('Enter IPPIS/Oracle ID')"
            type="text"
            for="ippis-oracle-id"
          />
        </b-col>
        <!--IPPIS/Oracle ID -->
      </b-row>
    </b-modal>
    <b-modal
      id="avatar-modal"
      ref="avatarModal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="avatarModalTitle"
      @hidden="resetAvatarModal"
      @cancel="resetAvatarModal"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <b-avatar
              size="30rem"
              :src="avatarLg"
            />
          </b-col>
        </b-row>
      </div>

      <template #modal-footer="{}">
        <div />
      </template>
    </b-modal>

    <b-modal
      id="info-modal"
      ref="infoModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-modal')"
    >
      <div class="p-2">
        <p>{{ $t('External Customer Account Reference') }}</p>
      </div>
    </b-modal>
    <b-modal
      id="info-customer-limit-modal"
      ref="infoCustomerLimitModal"
      centered
      size="md"
      ok-only
      :ok-title="$t('Okay')"
      @ok="hideModal('info-customer-limit-modal')"
    >
      <div class="p-0">
        <p>
          {{
            $t(
              'If enabled, then the limit entered will be checked against the special limit configured by the bank, instead of the general maximum account transaction limit. This check is to ensure the limit entered is not above the special limit configured'
            )
          }}
        </p>
      </div>
    </b-modal>
    <b-modal
      id="view-avatar-modal"
      ref="avatarModal"
      no-close-on-backdrop
      :ok-title="$t('Download File')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      :title="viewDocumentModalTitle"
      @hidden="resetDocumentModal"
      @cancel="resetDocumentModal"
      @ok="handleDownload(viewDocumentData)"
    >
      <div class="m-2 text-center">
        <b-row>
          <b-col cols="12">
            <div v-if="fileExtension === 'png' || fileExtension === 'jpeg' || fileExtension === 'jpg'">
              <b-img
                :src="viewDocumentData"
                block
                fluid
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'pdf'">
              <b-img
                :src="pdfIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else-if="fileExtension === 'vnd'">
              <b-img
                :src="wordIcon"
                block
                width="250px"
                height="250px"
                rounded
              />
            </div>
            <div v-else>
              <b-img src="" />
            </div>
          </b-col>
        </b-row>
      </div>
    </b-modal>
    <b-modal
      id="account-limit-modal"
      ref="account-limit-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Customer Transfer Limit')"
      ok-only
    >
      <validation-observer ref="accountLimitRules">
        <b-form @submit.prevent="saveCustomerAccountLimit">
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('Account Details')"
              class="card-item"
              is-visible
            >
              <b-row>
                <b-col md="4">
                  <InputField
                    id="account-name"
                    v-model="casaBalanceFormData.accountName"
                    :label="$t('Account Name')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="casaBalanceFormData.accountNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="branch"
                    v-model="casaBalanceFormData.branchName"
                    :label="$t('Branch')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="casaBalanceFormData.accountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="available-balance"
                    v-model="casaBalanceFormData.availableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="withdrawable-balance"
                    v-model="casaBalanceFormData.withdrawableBalance"
                    :label="$t('Withdrawable Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>

          <h5 class="text-capitalize mb-3">
            {{ $t('Transfer Limit Details') }}
          </h5>
          <b-row class="mb-2">
            <b-col md="6">
              <div class="d-flex">
                <SwitchButton
                  v-model="accountLimitFormData.hasSpecialConsideration"
                  :label="$t('Apply Special Limit Consideration?')"
                  name="special-consideration"
                />
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="align-middle text-body"
                  @click="onViewCustomerLimitInfo"
                />
              </div>
            </b-col>
          </b-row>
          <b-row v-if="!accountLimitFormData.hasSpecialConsideration">
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="single-transaction-limit"
                  v-model="accountLimitFormData.user_Single_Transaction_Limit"
                  :label="`${$t('Single Transaction Limit - Institution limit:')} ${toBalance(
                    institutionData.max_Single_Account_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Single Transaction Limit')"
                  :error-name="$t('Single Transaction Limit')"
                  :rules="`required|limit-amount:${institutionData.max_Single_Account_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="daily-transaction-limit"
                  v-model="accountLimitFormData.user_Daily_Transaction_Limit"
                  :label="`${$t('Daily Transaction Limit - Institution limit:')} ${toBalance(
                    institutionData.max_Daily_Account_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Daily Transaction Limit')"
                  :error-name="$t('Daily Transaction Limit')"
                  :rules="`required|limit-amount:${institutionData.max_Daily_Account_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
          </b-row>
          <b-row v-else>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="single-transaction-limit"
                  v-model="accountLimitFormData.user_Single_Transaction_Limit"
                  :label="`${$t('Single Transaction Limit - Institution Special limit:')} ${toBalance(
                    institutionData.specialLimit_Max_Single_Account_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Single Transaction Limit')"
                  :error-name="$t('Single Transaction Limit')"
                  :rules="`required|special-limit-amount:${institutionData.specialLimit_Max_Single_Account_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="daily-transaction-limit"
                  v-model="accountLimitFormData.user_Daily_Transaction_Limit"
                  :label="`${$t('Daily Transaction Limit - Institution Special limit:')} ${toBalance(
                    institutionData.specialLimit_Max_Daily_Account_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Daily Transaction Limit')"
                  :error-name="$t('Daily Transaction Limit')"
                  :rules="`required|special-limit-amount:${institutionData.specialLimit_Max_Daily_Account_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
          </b-row>

          <div class="button-center mt-5">
            <div>
              <Button
                :label="buttonLabel"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveCustomerAccountLimit"
              />
            </div>
          </div>
        </b-form>
      </validation-observer>
      <template #modal-footer="{}">
        <div />
      </template>
    </b-modal>

    <!-- USSD Limit Modal -->
    <b-modal
      id="ussd-limit-modal"
      ref="ussd-limit-modal"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('USSD Transfer Limit')"
      ok-only
    >
      <validation-observer ref="ussdLimitRules">
        <b-form @submit.prevent="saveUssdTransactionLimit">
          <app-collapse
            class="business-items mb-4"
            type="margin"
            accordion
          >
            <app-collapse-item
              :title="$t('USSD Details')"
              class="card-item"
              is-visible
            >
              <b-row>
                <b-col md="4">
                  <InputField
                    id="account-name"
                    v-model="casaBalanceFormData.accountName"
                    :label="$t('Account Name')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="account-number"
                    v-model="casaBalanceFormData.accountNumber"
                    :label="$t('Account Number')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="branch"
                    v-model="casaBalanceFormData.branchName"
                    :label="$t('Branch')"
                    type="text"
                    readonly
                  />
                </b-col>

                <b-col md="4">
                  <InputField
                    id="account-status"
                    v-model="casaBalanceFormData.accountStatus"
                    :label="$t('Account Status')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="available-balance"
                    v-model="casaBalanceFormData.availableBalance"
                    :label="$t('Available Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="withdrawable-balance"
                    v-model="casaBalanceFormData.withdrawableBalance"
                    :label="$t('Withdrawable Balance')"
                    type="text"
                    readonly
                  />
                </b-col>
                <b-col md="4">
                  <InputField
                    id="phone-number"
                    v-model="casaBalanceFormData.phoneNumber"
                    :label="$t('Phone Number')"
                    type="text"
                    readonly
                  />
                </b-col>
              </b-row>
            </app-collapse-item>
          </app-collapse>

          <h5 class="text-capitalize mb-3">
            {{ $t('Transfer Limit Details') }}
          </h5>
          <b-row class="mb-2">
            <b-col md="6">
              <div class="d-flex">
                <SwitchButton
                  v-model="ussdLimitFormData.hasSpecialConsideration"
                  :label="$t('Apply Special Limit Consideration?')"
                  name="special-consideration"
                />
                <feather-icon
                  icon="InfoIcon"
                  size="16"
                  class="align-middle text-body"
                  @click="onViewCustomerLimitInfo"
                />
              </div>
            </b-col>
          </b-row>
          <b-row v-if="!ussdLimitFormData.hasSpecialConsideration">
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="single-transaction-limit"
                  v-model="ussdLimitFormData.user_Single_Transaction_Limit"
                  :label="`${$t('Single Transaction Limit - Institution limit:')} ${toBalance(
                    institutionData.max_Single_USSD_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Single Transaction Limit')"
                  :error-name="$t('Single Transaction Limit')"
                  :rules="`required|limit-amount:${institutionData.max_Single_USSD_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="daily-transaction-limit"
                  v-model="ussdLimitFormData.user_Daily_Transaction_Limit"
                  :label="`${$t('Daily Transaction Limit - Institution limit:')} ${toBalance(
                    institutionData.max_Daily_USSD_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Daily Transaction Limit')"
                  :error-name="$t('Daily Transaction Limit')"
                  :rules="`required|limit-amount:${institutionData.max_Daily_USSD_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
          </b-row>
          <b-row v-else>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="single-transaction-limit"
                  v-model="ussdLimitFormData.user_Single_Transaction_Limit"
                  :label="`${$t('Single Transaction Limit - Institution Special limit:')} ${toBalance(
                    institutionData. specialLimit_Max_Single_USSD_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Single Transaction Limit')"
                  :error-name="$t('Single Transaction Limit')"
                  :rules="`required|special-limit-amount:${institutionData.specialLimit_Max_Single_USSD_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
            <b-col md="6">
              <ValidationObserver>
                <InputNumberField
                  id="daily-transaction-limit"
                  v-model="ussdLimitFormData.user_Daily_Transaction_Limit"
                  :label="`${$t('Daily Transaction Limit - Institution Special limit:')} ${toBalance(
                    institutionData.specialLimit_Max_Daily_USSD_Transaction_Limit * 100
                  )}`"
                  :placeholder="$t('Enter Daily Transaction Limit')"
                  :error-name="$t('Daily Transaction Limit')"
                  :rules="`required|special-limit-amount:${institutionData.specialLimit_Max_Daily_USSD_Transaction_Limit}|positive`"
                />
              </ValidationObserver>
            </b-col>
          </b-row>

          <div class="button-center mt-5">
            <div>
              <Button
                :label="buttonLabel"
                :loading="isLoading"
                :disabled="isDisabled"
                size="lg"
                @click="saveUssdTransactionLimit"
              />
            </div>
          </div>
        </b-form>
      </validation-observer>
      <template #modal-footer="{}">
        <div />
      </template>
    </b-modal>
    <!-- USSD Limit Modal -->

    <b-modal
      id="memo-info-modal"
      ref="memoInfoModal"
      :ok-title="$t('Cancel')"
      ok-only
      :title="$t('Memo')"
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
    >
      <b-table
        ref="memoListTable"
        select-mode="single"
        :items="memoGetData"
        responsive
        :fields="translatedMemoListHeaders"
        primary-key="id"
        selectable
        show-empty
        :empty-text="$t('No data available')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #cell(SN)="data">
          {{ getOverallIndex(data.index) }}
        </template>
      </b-table>
    </b-modal>
    <b-modal
      id="add-reason-modal"
      ref="addReason-modal"
      :ok-title="$t('Submit')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Add A Reason')"
      modal-footer
      @ok="manageAccountStatus"
      @cancel="resetReasonForm"
    >
      <b-row class="match-height">
        <b-col md="12">
          <TextAreaField
            id="reason"
            v-model="reasonFormData.reason"
            :label="$t('Reason')"
            :placeholder="$t('What\'s your Reason?')"
            type="text"
            rows="5"
            for="reason"
          />
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      id="activate-deactivate-reason-modal"
      ref="addActivateDeactivate-modal"
      :ok-title="$t('Submit')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="lg"
      :title="$t('Add A Reason')"
      modal-footer
      @ok="manageCustomerAccountStatus"
      @cancel="resetReasonForm"
    >
      <b-row class="match-height">
        <b-col md="12">
          <TextAreaField
            id="reason"
            v-model="reasonFormData.reason"
            :label="$t('Reason')"
            :placeholder="$t('What\'s your Reason?')"
            type="text"
            rows="5"
            for="reason"
          />
        </b-col>
      </b-row>
    </b-modal>
    <b-modal
      id="customer-account-transactions-modal"
      ref="customer-account-transactions-modal"
      :ok-title="$t('Cancel')"
      ok-only
      :title="$t('Customer Account Transactions')"
      ok-variant="outline-secondary"
      centered
      no-close-on-backdrop
      size="xl"
    >
      <div class="mb-2 mt-3 d-flex align-items-center">
        <TableEntries
          v-model="transactionPerPage"
          :options="perPageOptions"
        />
      </div>
      <b-table
        ref="transactionTableRef"
        :fields="transactionTableHeaders"
        :items="accountTransactionData"
        :busy="isBusy"
        :per-page="transactionPageSize"
        :current-page="transactionCurrentPage"
        responsive
        primary-key="id"
        show-empty
        :empty-text="$t('No records available')"
        :sort-by.sync="sortBy"
        :sort-desc.sync="sortDesc"
        class="position-relative"
      >
        <template #table-busy>
          <div class="text-center text-danger my-2">
            <b-spinner
              class="align-middle"
              variant="primary"
            />
          </div>
        </template>

        <template #cell(SN)="data">
          {{ getTableNumber(data.index) }}
        </template>

        <template #cell(financialDate)="data">
          <p class="mb-0">
            {{ data.value ? formatCustomDate(data.value) : '' }}
          </p>
        </template>

        <template #cell(transactionDate)="data">
          <p class="mb-0">
            {{ data.value ? formatDateTime(data.value) : '' }}
          </p>
        </template>

        <template #cell(debit)="data">
          <span class="text-nowrap">
            {{ data.value ? toBalance(data.value) : '' }}
          </span>
        </template>

        <template #cell(credit)="data">
          <span class="text-nowrap">
            {{ data.value ? toBalance(data.value) : '' }}
          </span>
        </template>

        <template #cell(balance)="data">
          <span class="text-nowrap">
            {{ data.value ? toBalance(data.value) : 0 }}
          </span>
        </template>
      </b-table>

      <TablePagination
        v-model="transactionCurrentPage"
        :from="transactionFrom"
        :to="transactionTo"
        :total-rows="transactionTotalEntries"
        :per-page="transactionPerPage"
      />
    </b-modal>
    <FullScreenSpinner :show="showSpinner" />
  </b-card>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BFormCheckbox,
  BMedia,
  BAvatar,
  BTable,
  BSpinner,
  BForm,
  BTabs,
  BTab,
  BBadge,
  BDropdown,
  BDropdownItem,
  BFormGroup,
  BFormFile,
  BImg,
  BButton,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { mapActions, mapGetters } from 'vuex'
import { required } from '@validations'
import Ripple from 'vue-ripple-directive'
import { modalMixins } from '@core/mixins/modal'
import { tableMixins } from '@core/mixins/table'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { permissionMixin } from '@core/mixins/permission'
import { toastAlert } from '@core/mixins/toast'
import AppCollapse from '@core/components/app-collapse/AppCollapse.vue'
import AppCollapseItem from '@core/components/app-collapse/AppCollapseItem.vue'
import TablePagination from '@/views/components/table/TablePagination.vue'
import FullScreenSpinner from '@/views/components/spinner/FullScreenSpinner.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import getStatusColor from '@/helpers/getStatusColor'
import { toDateWithYear, toCustomDateTime, toCustomDate } from '../../../../../helpers/formatDate'
import InputField from '../../../../forms/InputField.vue'
import SelectField from '../../../../forms/SelectField.vue'
import Button from '../../../../components/button/Button.vue'
import {
  toBalance, toKobo, toAmount, formatAmount,
} from '../../../../../helpers/formatAmount'
import {
  accountStatus,
  accountTier,
  users,
  accessLevel,
  deliveryMode,
  frequency,
  accountTypes,
  accountCustomerTypes,
  categoryOfAccountOptions,
  sectorCodeOptions,
  applyInterest,
  conditionalOptions,
  pdfIcon,
  wordIcon,
} from '../../../../../helpers/data'
import InputNumberField from '../../../../forms/InputNumberField.vue'
import { transformMandateRule } from '../../../../../helpers/transformers'
import { accountPermissions, eChannelsPermission } from '../../../../../helpers/permissions'
import SwitchButton from '../../../../components/switch/Switch.vue'
import convertZeroToString from '@/helpers/convertZeroToString'
import TextAreaField from '../../../../forms/TextAreaField.vue'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BBadge,
    BForm,
    BTabs,
    BTab,
    BFormCheckbox,
    BMedia,
    BAvatar,
    InputField,
    InputNumberField,
    SwitchButton,
    SelectField,
    TextAreaField,
    BTable,
    BSpinner,
    BDropdown,
    BDropdownItem,
    TablePagination,
    FullScreenSpinner,
    TableEntries,
    Button,
    BFormGroup,
    BFormFile,
    BImg,
    BButton,
    AppCollapse,
    AppCollapseItem,
    ValidationObserver,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, toastAlert, permissionMixin],
  data() {
    return {
      formatDateTime: toCustomDateTime,
      formatCustomDate: toCustomDate,
      toDateWithYear,
      sortBy: '',
      searchQuery: '',
      required,
      sortDesc: false,
      toAmount,
      toBalance,
      isDisabled: false,
      isLoading: false,
      buttonLabel: this.$t('Save'),
      showSpinner: false,
      isCreditInterest: false,
      getStatusColor,
      categoryOfAccountOptions,
      sectorCodeOptions,
      applyOptions: applyInterest,
      conditionalOptions,
      signatoryListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'fullName', sortable: true, label: 'Full Name' },
        { key: 'passport', sortable: true, label: 'Passport' },
        { key: 'signature', sortable: true, label: 'Signature' },
      ],
      groupMembersHeader: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'name', sortable: true, label: 'Name' },
        { key: 'phoneNumber', sortable: true, label: 'Phone Number' },
        { key: 'email', sortable: true, label: 'Email' },
      ],
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'customerID', sortable: true, label: 'Customer ID' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'referenceNumber', sortable: true, label: 'Reference Number' },
        { key: 'accountOfficerName', sortable: true, label: 'Account Officer Name' },
        { key: 'accountType', sortable: true, label: 'Account Type' },
        { key: 'customerType', sortable: true, label: 'Customer Type' },
        { key: 'currencyCode', sortable: true, label: 'Currency' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'productName', sortable: true, label: 'Product Name' },
        { key: 'branchName', sortable: true, label: 'Branch Name' },
        { key: 'dateCreated', sortable: true, label: 'Date Created' },
        { key: 'accountStatus', sortable: true, label: 'Account Status' },
        { key: 'actions' },
      ],
      mandateListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'rule', sortable: true, label: 'Rule' },
        { key: 'isLimited', sortable: true, label: 'Limit' },
        { key: 'amount', sortable: true, label: 'Amount' },
      ],
      customerAccountList: [],
      productTypeOptions: [],
      accountOfficerOptions: [],
      allAccountOfficerOptions: [],
      signatoryList: [],
      signatoryMandateList: users,
      mandateList: [],
      filter: {
        accountName: '',
        accountNumber: '',
        referenceNumber: '',
        accountType: '',
        accountOfficerID: '',
        customerType: '',
        customerID: '',
        branchCode: '',
        currencyCode: '',
        accountStatus: '',
        bvn: '',
        sortColumn: '',
        accessLevel: null,
        productCode: '',
        employeeID: '',
      },
      refereeInformation: {},
      accountIntroducerList: {},
      customer: {
        customerID: '',
        accountName: '',
        accountType: '',
        branchCode: '',
        productCode: '',
        accountStatus: '',
        accountOfficerID: '',
        accountTierLevel: '',
        accessLevel: '',
        sectorCode: '',
        categoryOfAccount: '',
        signatories: {},
        overrideProductApplicableFee: false,
        applicableFee: 0,
        overrideProductApplicableInterest: false,
        applicableInterest: 0,
        statementDeliveryMode: '',
        statementDeliveryFrequency: '',
        enableEmailNotification: false,
        enableSMSNotification: false,
        minimumBalanceRequired: 0,
        accountCreationChannel: null,
        employeeID: '',
        notificationPhoneNumber: '',
        notificationEmail: '',
        mandateRuleConfig: {
          ruleConfig: [],
        },
        interestRate: null,
        applyCreditInterest: '',
        productFees: [],
        productInterests: [],
        applyFee: '',
        flatAmount: null,
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        minimumAmount: null,
        maximumAmount: null,
        groupConnectionID: null,
        groupConnectionName: null,
      },
      timeout: null,
      feeConditionBasis: '',
      isApplyFee: false,
      isFeeNoOfWithdrawal: false,
      interestConditionBasis: '',
      isConditional: false,
      isConditionalFee: false,
      showAddProductInterest: false,
      productOptions: [],
      casaBalanceFormData: {
        accountNumber: '',
        accountName: '',
        productName: '',
        accountStatus: '',
        accountOfficerName: '',
        branchName: '',
        overdraftLimit: '',
        unclearedEffect: '',
        ledgerBalance: '',
        availableBalance: '',
        withdrawableBalance: 0,
        overdrawnAmount: 0,
        unpaidOverdraftInterest: 0,
        unpaidAccountOpeningFee: 0,
        unpaidAccountMaintenanceFee: 0,
        unpaidCOTFee: 0,
        unpaidOverdraftFee: 0,
        unpaidSMSFee: 0,
        totalOutstandingOverdraft: 0,
        unpaidLoanPrincipal: 0,
        unpaidLoanInterest: 0,
        unpaidLoanFee: 0,
        unpaidLoanPenalty: 0,
        totalOutstandingLoan: 0,
        accruedCOTFee: 0,
        accruedSMSFee: 0,
        accruedLoanInterest: 0,
        accruedOverdraftInterest: 0,
        totalAccruedAmount: 0,
        securityDeposit: 0,
        productMinimumBalance: 0,
        totalWithheldAmount: 0,
        percentageLien: 0,
        flatLien: 0,
        totalLien: 0,
        repaymentLien: 0,
        defaultingLoanInterest: 0,
        phoneNumber: '',
      },
      accountLimitFormData: {
        accountNumber: '',
        user_Daily_Transaction_Limit: '',
        user_Single_Transaction_Limit: '',
        hasSpecialConsideration: false,
      },
      // USSD
      ussdLimitFormData: {
        phoneNumber: '',
        user_Daily_Transaction_Limit: '',
        user_Single_Transaction_Limit: '',
        hasSpecialConsideration: false,
      },

      institutionData: {
        max_Single_Account_Transaction_Limit: 0,
        max_Daily_Account_Transaction_Limit: 0,
        specialLimit_Max_Single_Account_Transaction_Limit: 0,
        specialLimit_Max_Daily_Account_Transaction_Limit: 0,
      },
      avatarLg: '',
      avatarModalTitle: '',
      accountTierOptions: accountTier,
      accessLevelOptions: accessLevel,
      deliveryModeOptions: deliveryMode,
      frequencyOptions: frequency,
      groupMemberList: [],

      tablePageSize: 0,
      tableCurrentPage: 1,
      tablePerPage: 10,
      recordCount: 0,

      pdfIcon,
      wordIcon,
      documentsFormData: [
        {
          type: '',
          url: '',
          file: '',
        },
      ],
      viewDocumentData: '',
      viewDocumentModalTitle: '',
      fileExtension: '',
      documentTypeOptions: [
        { value: 1, label: 'Signature Specimen' },
        { value: 2, label: 'Proof Of Address' },
        { value: 3, label: 'Driver’s License' },
        { value: 4, label: 'National Identity Card' },
        { value: 5, label: 'Voters Card' },
        { value: 6, label: 'National Identity Number' },
        { value: 7, label: 'International Passport' },
        { value: 8, label: 'Passport Photograph' },
        { value: 9, label: 'Reference Letter' },
        { value: 10, label: 'Guarantor’s Letter' },
        { value: 11, label: 'Loan Application Form' },
        { value: 12, label: 'Credit Bureau Form' },
        { value: 13, label: 'Others' },
      ],
      documentIdentifier: '',
      currencyCodeOptions: [],
      memoListHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', label: 'Account Number' },
        { key: 'actionType', label: 'Action Type' },
        { key: 'details', label: 'Reason' },
        { key: 'comments', label: 'Comments' },
        { key: 'initiatedBy', label: 'Initiated By' },
      ],
      memoGetData: [],
      reasonFormData: {
        formData: '',
        route: '',
        reason: '',
        items: '',
      },
      transactionTableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'financialDate', sortable: true, label: 'Financial Date' },
        { key: 'transactionDate', sortable: true, label: 'Transaction Date' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'postedBy', sortable: true, label: 'Posted By' },
        { key: 'postingReferenceNumber', sortable: true, label: 'Posting Reference Number' },
        { key: 'debit', sortable: true, label: 'Debit' },
        { key: 'credit', sortable: true, label: 'Credit' },
        { key: 'narration', sortable: true, label: 'Narration' },
        { key: 'entryCode', sortable: true, label: 'Sort Code' },
        { key: 'instrumentNumber', sortable: true, label: 'Instrument Number' },
        { key: 'balance', sortable: true, label: 'Balance' },
      ],
      accountTransactionData: [],
      transactionPerPage: 10,
      transactionPageSize: 0,
      transactionCurrentPage: 1,
      transactionTotalCount: 0,

      accountNumber: '',
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
    translatedMandateListHeaders() {
      return this.mandateListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedGroupMembersHeader() {
      return this.groupMembersHeader.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedSignatoryListHeaders() {
      return this.signatoryListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    translatedMemoListHeaders() {
      return this.memoListHeaders.map(header => ({
        ...header,
        label: header.label ? this.$t(header.label) : header.key,
      }))
    },
    formattedDocumentTypeOptions() {
      return this.documentTypeOptions.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    accountTypeOptions() {
      return accountTypes.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    accountStatusOptions() {
      return accountStatus.map(item => ({ label: this.$t(item.label), value: item.value }))
    },
    customerTypeOptions() {
      return accountCustomerTypes.map(item => ({ label: this.$t(item.label), value: item.value }))
    },

    transactionTotalEntries() {
      return this.transactionTotalCount
    },
    transactionNumberOfPages() {
      return Math.floor((this.transactionTotalEntries + this.transactionPerPage - 1) / this.transactionPerPage)
    },
    transactionFrom() {
      if (this.transactionTotalCount === 0) {
        return 0
      }
      return this.transactionCurrentPage * this.transactionPerPage - (this.transactionPerPage - 1)
    },
    transactionTo() {
      return Math.min(this.transactionFrom + this.transactionPerPage - 1, this.transactionTotalEntries)
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchCustomerAccount()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchCustomerAccount()
    },
    transactionCurrentPage(newValue) {
      this.transactionCurrentPage = newValue
      this.setCustomerAccountHistories(this.accountNumber)
    },
    transactionPerPage(newValue) {
      this.transactionPerPage = newValue
      this.setCustomerAccountHistories(this.accountNumber)
    },
  },
  beforeDestroy() {
    clearTimeout(this.timeout)
  },
  mounted() {
    this.fetchCustomerAccount()
    // this.fetchBranches()
    this.fetchProducts()
    this.fetchAccountOfficers()
    this.fetchCASAProducts()
    this.fetchAllCurrencies()
    this.fetchInstitutionLimitDetails()
  },
  methods: {
    ...mapActions({
      getCustomerAccount: 'account/getCustomerAccountList',
      getCustomerAccountById: 'account/getCustomerAccountAction',
      updateCustomerAccount: 'account/updateCustomerAccountAction',
      getUserBalance: 'account/getCASABalanceAction',
      updateCustomerAccountStatus: 'account/updateStatus',
      getProducts: 'account/getProducts',
      getAccountOfficers: 'account/getAccountOfficerList',
      getAccountSignatories: 'account/getAccountSignatoriesAction',
      getAccountReferees: 'account/getAccountRefereesAction',
      getGroupMembers: 'account/getGroupMembersByGroupId',
      getCustomerInformation: 'account/getCustomerInformation',
      getAccountsByCustomerID: 'account/getAccountsByCustomerIDAction',
      getAccountOfficerByStaffID: 'account/getAccountOfficerByStaffID',
      toggleCustomerAccountStatus: 'account/toggleCustomerAccountStatus',
      getCASAProduct: 'account/getProducts',
      getAllCurrencies: 'account/getAllCurrenciesAction',

      getAccountLimit: 'eChannels/getAccountLimitDetails',
      getInstitutionLimitDetails: 'eChannels/getInstitutionLimitDetails',
      createAccountLimit: 'eChannels/createAccountLimit',
      updateAccountLimit: 'eChannels/updateAccountLimit',
      getAccountHistories: 'reports/getTransactionReports',
      addOrUpdate: 'eChannels/addOrUpdate',
      getUssdLimitDetails: 'eChannels/getUssdLimitDetails',

    }),
    async fetchAllCurrencies() {
      try {
        const response = await this.getAllCurrencies({ permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT })
        const { status, data } = await response
        if (status === 200 && data.data) {
          this.currencyCodeOptions = data.data
            .filter(item => item.status === 'Active')
            .reduce((acc, curr) => [...acc, curr.code], [])
        }
      } catch (error) {
        this.handleError(error)
      }
    },
    handleDownload(data) {
      const link = document.createElement('a')
      link.href = data
      link.click()
      this.alertSuccess({ text: this.$t('Your request to download file was successful') })
    },
    viewDocumentFile(image, title, fileExtension) {
      this.fileExtension = fileExtension
      this.viewDocumentData = image
      this.viewDocumentModalTitle = title.replace(/([a-zA-Z])(?=[A-Z])/g, '$1 ')
      this.$root.$emit('bv::show::modal', 'view-avatar-modal')
    },
    resetDocumentModal() {
      this.viewDocumentData = ''
      this.viewDocumentModalTitle = ''
    },
    removeProductInterestItem(index) {
      this.customer.productInterests.splice(index, 1)
    },
    removeProductFeeItem(index) {
      this.customer.productFees.splice(index, 1)
    },
    repeatProductInterest() {
      this.customer.productInterests.push({
        interestConditionValue: null,
        // interestCode: null,
        // interestID: null,
        interestRate: null,
        useDefaultInterestRate: true,
      })
    },
    repeatProductFees() {
      this.customer.productFees.push({
        // feeCode: '',
        // feeID: null,
        flatAmount: null,
        flatAmountPerTransaction: null,
        percentageOnTurnover: null,
        minimumAmount: null,
        maximumAmount: null,
        feeConditionValue: null,
      })
    },
    async fetchCASAProducts() {
      try {
        const response = await this.getCASAProduct({
          search: { type: 'Savings' },
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.productOptions = data.data
            .filter(item => item.status === 'Active')
            .map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async fetchAccountOfficers() {
      try {
        const { data, status } = await this.getAccountOfficers({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data
            .filter(item => item.status === 'Active')
            .map(item => ({ label: item.name, value: item.id }))
          this.allAccountOfficerOptions = data.data.map(item => ({ label: item.name, value: item.id }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    setApplyFee(selectedValue) {
      if (selectedValue === 'True') {
        this.isApplyFee = true
        this.isConditionalFee = false
        this.customer.productFees = []
      } else if (selectedValue === 'False') {
        this.isApplyFee = false
        this.isConditionalFee = false
        this.customer.productFees = []
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      } else {
        this.isApplyFee = false
        this.isConditionalFee = true
        this.customer.flatAmount = null
        this.customer.flatAmountPerTransaction = null
        this.customer.percentageOnTurnover = null
        this.customer.maximumAmount = null
        this.customer.minimumAmount = null
      }
    },
    setApplyCreditInterest(selectedValue) {
      if (selectedValue === 'True') {
        this.isCreditInterest = true
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.productInterests = []
      } else if (selectedValue === 'False') {
        this.isCreditInterest = false
        this.isConditional = false
        this.showAddProductInterest = false
        this.customer.interestRate = 0
        this.customer.productInterests = []
      } else {
        this.isCreditInterest = false
        this.isConditional = true
        this.customer.interestRate = 0
      }
    },
    onViewPassport(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Passport')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    onViewSignature(image, name) {
      this.avatarLg = image
      this.avatarModalTitle = `${name}'s ${this.$t('Signature')}`
      this.$root.$emit('bv::show::modal', 'avatar-modal')
    },
    setDefaultApplicableInterest(value) {
      if (!value) {
        this.customer.applicableInterest = null
        return false
      }
      return true
    },
    setProductApplicableFee(value) {
      if (!value) {
        this.customer.applicableFee = null
        return false
      }
      return true
    },
    async fetchAccountReferees(accountNumber) {
      try {
        const { data, status } = await this.getAccountReferees({
          accountNumber,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200 && data.data) {
          this.refereeInformation = data.data.reduce((acc, item, index) => {
            const dynamicKey = `referee${index + 1}`
            return {
              ...acc,
              [dynamicKey]: `${item.lastName} ${item.firstName} ${item.otherNames} - ${item.customerID}`,
            }
          }, {})
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountOfficerByStaffID(staffID) {
      try {
        const response = await this.getAccountOfficerByStaffID({
          staffID,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.accountIntroducerList = {
            ...data.data,
            customerName: data.data.name,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountByCustomerID(customerID) {
      try {
        const response = await this.getAccountsByCustomerID({
          customerID,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        const { data, status } = response
        if (status === 200 && data.data !== null) {
          this.accountIntroducerList = {
            ...data.data,
            customerName: `${data.data?.lastName ?? ''} ${data.data?.firstName ?? ''} ${data.data?.otherNames ?? ''}`,
          }
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    gotoAddCustomerAccount() {
      this.$router
        .push({
          name: 'current-savings-account',
          params: {
            type: 'add',
            currencyOptions: this.currencyCodeOptions,
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
    onViewInfo() {
      this.$root.$emit('bv::show::modal', 'info-modal')
    },
    onViewCustomerLimitInfo() {
      this.$root.$emit('bv::show::modal', 'info-customer-limit-modal')
    },
    async onSelectVirtualAccount(items, activity, val) {
      this.$router
        .push({
          name: 'current-savings-account',
          params: {
            type: val,
            items,
          },
        })
        .catch(error => {
          this.alertError({ text: error })
        })
    },
    async onSelectCustomerAccountItem(items, activity, val) {
      try {
        if (val === 'edit') {
          this.$router
            .push({
              name: 'current-savings-account',
              params: {
                type: val,
                id: items.id,
              },
            })
            .catch(error => {
              this.alertError({ text: error })
            })
        }
        this.showSpinner = true
        this.groupMemberList = []
        if (items.mandateRuleIndex !== null) {
          this.mandateList = transformMandateRule(items.mandateRuleIndex)
        }
        const { data, status } = await this.getCustomerAccountById({
          id: items.id,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        this.documentIdentifier = data.data.documentIdentifier
        this.documentsFormData = data.data.documents
          ? data.data.documents.map(item => ({
            url: item.url,
            type: item.typeID,
            id: item.id,
            fileName: item.type,
            fileExtension: item.fileExtension,
          }))
          : [
            {
              url: '',
              type: '',
            },
          ]

        this.fetchAccountReferees(items.accountNumber)

        if (items.introducerType === 'BankStaff') {
          this.fetchAccountOfficerByStaffID(items.introducerID)
        }
        if (items.introducerType === 'Customer') {
          this.fetchAccountByCustomerID(items.introducerID)
        }
        // const customerInfo = await this.getAccountSignatories(items.accountNumber)

        if (items.isGroupAccount) {
          const customerSignatory = await this.getAccountSignatories({
            accountNumber: items.accountNumber,
            permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          })

          this.signatoryList = customerSignatory.data.data
            ? customerSignatory.data.data.map(item => ({
              fullName: item.name,
              passport: item.passportFileUrl,
              signature: item.signatureFileUrl,
            }))
            : []
        }

        if (!items.isGroupAccount) {
          const customerSignatory = await this.getCustomerInformation({
            customerID: items.customerID,
            permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          })
          this.signatoryList = [
            {
              fullName: customerSignatory.data.data.name,
              passport: customerSignatory.data.data.passportFileUrl,
              signature: customerSignatory.data.data.signatureFileUrl,
            },
          ]
        }

        if (items.isGroupAccount) {
          const response = await this.getGroupMembers({
            formData: {
              groupCustomerID: items.customerID,
              pageNumber: 0,
              pageSize: 0,
            },
            permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          })

          this.groupMemberList = response.data.data
          this.recordCount = response.data.recordCount
        }
        if (status === 200) {
          this.timeout = setTimeout(() => {
            let productInterests
            let productFees
            this.customer.applyCreditInterest = data.data.applyCreditInterest
            this.customer.applyFee = data.data.applyFee

            if (data.data.applyCreditInterest === 'True') {
              this.customer.interestRate = data.data.interestRate
            }
            if (data.data.applyFee === 'True') {
              this.customer.flatAmount = data.data.flatAmount === 0 ? '0' : formatAmount(data.data.flatAmount)
              this.customer.flatAmountPerTransaction = data.data.flatAmountPerTransaction === 0 ? '0' : formatAmount(data.data.flatAmountPerTransaction)
              this.customer.percentageOnTurnover = data.data.percentageOnTurnover === 0 ? '0' : data.data.percentageOnTurnover
              this.customer.maximumAmount = data.data.maximumAmount === 0 ? '0' : formatAmount(data.data.maximumAmount)
              this.customer.minimumAmount = data.data.minimumAmount === 0 ? '0' : formatAmount(data.data.minimumAmount)
            }

            if (data.data.applyCreditInterest !== null) {
              this.setApplyCreditInterest(data.data.applyCreditInterest)
              // this.customer.applyCreditInterest = data.data.applyCreditInterest

              if (data.data?.productInterests && data.data?.productInterests.length > 0) {
                this.interestConditionBasis = data.data?.productInterests[0].interestConditionBasis
                this.isInterestNoOfWithdrawal = data.data?.productInterests[0].interestConditionBasis === 'NoOfWithdrawals'

                productInterests = data.data?.productInterests?.map(item => ({
                  ...item,
                  interestConditionValue: !this.isInterestNoOfWithdrawal
                    ? formatAmount(item.interestConditionValue)
                    : item.interestConditionValue,
                  interestRate: convertZeroToString(item.interestRate),
                }))

                this.customer.productInterests = productInterests
              }
            }

            if (data.data.applyFee !== null) {
              this.setApplyFee(data.data.applyFee)
              if (data.data?.productFees && data.data?.productFees.length > 0) {
                this.feeConditionBasis = data.data?.productFees[0].feeConditionBasis
                this.isFeeNoOfWithdrawal = data.data?.productFees[0].feeConditionBasis === 'NoOfWithdrawals'
                productFees = data.data?.productFees?.map(item => ({
                  ...item,
                  flatAmount: item.flatAmount === 0 ? '0' : formatAmount(item.flatAmount),
                  flatAmountPerTransaction:
                    item.flatAmountPerTransaction === 0 ? '0' : formatAmount(item.flatAmountPerTransaction),
                  percentageOnTurnover: convertZeroToString(item.percentageOnTurnover),
                  minimumAmount: item.minimumAmount === 0 ? '0' : formatAmount(item.minimumAmount),
                  maximumAmount: item.maximumAmount === 0 ? '0' : formatAmount(item.maximumAmount),
                  feeConditionValue: !this.isFeeNoOfWithdrawal
                    ? formatAmount(item.flatAmount)
                    : convertZeroToString(item.feeConditionValue),
                }))

                this.customer.productFees = productFees
              }
            }
          }, 1000)

          this.customer = {
            ...items,
            minimumBalanceRequired: items.minimumBalanceRequired ? toBalance(items.minimumBalanceRequired) : null,
            overrideProductApplicableInterest: data.data.overrideProductApplicableInterest,
            notificationEmail: data.data.notificationEmail,
            notificationPhoneNumber: data.data.notificationPhoneNumber,
          }
        }
        this.modalTitle = this.$t('Customer Account')
        this.showSpinner = false
        this.$root.$emit('bv::show::modal', 'modal-lg')

        if (data.data.memos !== null) {
          this.memoGetData = data.data.memos
          this.$root.$emit('bv::show::modal', 'memo-info-modal')
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    groupMemberTableIndex(index) {
      return this.tableCurrentPage * this.tablePerPage - this.tablePerPage + index + 1
    },
    async fetchCustomerAccount() {
      this.isBusy = true
      this.hideModal('modal-filter')
      try {
        const { status, data } = await this.getCustomerAccount({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200 && data.data) {
          this.customerAccountList = data.data
          this.totalCount = data.recordCount
        }
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.isBusy = false
      }
    },
    async getCASABalance(items) {
      try {
        this.showSpinner = true
        const { data, status } = await this.getUserBalance({
          accountNumber: items.accountNumber,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200) {
          this.casaBalanceFormData = {
            ...data,
            accountNumber: data.accountNumber.toString(),
            unpaidOverdraftInterest: toBalance(data.unpaidOverdraftInterest),
            unpaidAccountOpeningFee: toBalance(data.unpaidAccountOpeningFee),
            unpaidAccountMaintenanceFee: toBalance(data.unpaidAccountMaintenanceFee),
            unpaidCOTFee: toBalance(data.unpaidCOTFee),
            unpaidOverdraftFee: toBalance(data.unpaidOverdraftFee),
            unpaidSMSFee: toBalance(data.unpaidSMSFee),
            totalOutstandingOverdraft: toBalance(data.totalOutstandingOverdraft),
            unpaidLoanPrincipal: toBalance(data.unpaidLoanPrincipal),
            unpaidLoanInterest: toBalance(data.unpaidLoanInterest),
            unpaidLoanFee: toBalance(data.unpaidLoanFee),
            unpaidLoanPenalty: toBalance(data.unpaidLoanPenalty),
            totalOutstandingLoan: toBalance(data.totalOutstandingLoan),
            accruedCOTFee: toBalance(data.accruedCOTFee),
            accruedSMSFee: toBalance(data.accruedSMSFee),
            accruedLoanInterest: toBalance(data.accruedLoanInterest),
            accruedOverdraftInterest: toBalance(data.accruedOverdraftInterest),
            securityDeposit: toBalance(data.securityDeposit),
            productMinimumBalance: toBalance(data.productMinimumBalance),
            totalWithheldAmount: toBalance(data.totalWithheldAmount),
            percentageLien: toBalance(data.percentageLien),
            overdraftLimit: toBalance(data.overdraftLimit),
            ledgerBalance: toBalance(data.ledgerBalance),
            availableBalance: toBalance(data.availableBalance),
            withdrawableBalance: toBalance(data.withdrawableBalance),
            flatLien: toBalance(data.flatLien),
            totalLien: toBalance(data.totalLien),
            repaymentLien: toBalance(data.repaymentLien),
            totalAccruedAmount: toBalance(data.totalAccruedAmount),
            overdrawnAmount: toBalance(data.overdrawnAmount),
            unclearedEffect: toBalance(data.unclearedEffect),
            defaultingLoanInterest: toBalance(data.defaultingLoanInterest),
          }
        }
        this.showSpinner = false
        this.$root.$emit('bv::show::modal', 'casa-balance-modal')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    async manageAccountLimit(items, value) {
      try {
        this.showSpinner = true
        const { data, status } = await this.getUserBalance({
          accountNumber: items.accountNumber,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200) {
          this.casaBalanceFormData = {
            ...data,
            accountNumber: data.accountNumber.toString(),
            ledgerBalance: toBalance(data.ledgerBalance),
            availableBalance: toBalance(data.availableBalance),
            withdrawableBalance: toBalance(data.withdrawableBalance),
            phoneNumber: items.phoneNumber,
          }
          this.fetchLimits()
          this.fetchUssdLimits()
        }
        this.showSpinner = false
        this.$root.$emit('bv::show::modal', value === 'transfer' ? 'account-limit-modal' : 'ussd-limit-modal')
      } catch (error) {
        this.alertError({ text: error })
      } finally {
        this.showSpinner = false
      }
    },
    getTableNumber(index) {
      return this.transactionCurrentPage * this.transactionPerPage - this.transactionPerPage + index + 1
    },
    async setCustomerAccountHistories(accountNumber) {
      try {
        this.accountNumber = accountNumber
        this.showSpinner = true
        const filter = {
          accountNumber,
        }
        const response = await this.getAccountHistories({
          search: filter,
          page: this.transactionCurrentPage,
          limit: this.transactionPerPage,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          route: 'customerHistoryReport',
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountTransactionData = data.data
          this.transactionTotalCount = data.recordCount
          this.isBusy = false
        } else {
          this.accountTransactionData = []
        }
        this.showSpinner = false
        this.$root.$emit('bv::show::modal', 'customer-account-transactions-modal')
      } catch (error) {
        this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },
    async fetchLimits() {
      try {
        this.showSpinner = true
        const response = await this.getAccountLimit({
          accountNumber: this.casaBalanceFormData.accountNumber,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        const { status, data } = await response
        if (status === 200 && data.user_Daily_Transaction_Limit > 0 && data.user_Daily_Transaction_Limit > 0) {
          this.accountLimitFormData = {
            user_Daily_Transaction_Limit: formatAmount(data.user_Daily_Transaction_Limit),
            user_Single_Transaction_Limit: formatAmount(data.user_Single_Transaction_Limit),
            hasSpecialConsideration: data.hasSpecialConsideration,
          }
          this.buttonLabel = this.$t('Update')
        } else {
          const res = await this.getInstitutionLimitDetails({
            permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          })
          this.accountLimitFormData = {
            user_Daily_Transaction_Limit: formatAmount(res.data.default_Max_Daily_Account_Transaction_Limit),
            user_Single_Transaction_Limit: formatAmount(res.data.default_Max_Single_Account_Transaction_Limit),
            hasSpecialConsideration: data.hasSpecialConsideration,
          }
          this.buttonLabel = this.$t('Update')
        }
      } catch (error) {
        this.handleError({ error })
      } finally {
        this.showSpinner = false
      }
    },
    async fetchInstitutionLimitDetails() {
      try {
        this.showSpinner = true
        const response = await this.getInstitutionLimitDetails({
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        const { status, data } = await response
        if (status === 200 && data) {
          this.institutionData = {
            max_Single_Account_Transaction_Limit: formatAmount(data.max_Single_Account_Transaction_Limit),
            max_Daily_Account_Transaction_Limit: formatAmount(data.max_Daily_Account_Transaction_Limit),
            specialLimit_Max_Single_Account_Transaction_Limit: formatAmount(
              data.specialLimit_Max_Single_Account_Transaction_Limit,
            ),
            specialLimit_Max_Daily_Account_Transaction_Limit: formatAmount(
              data.specialLimit_Max_Daily_Account_Transaction_Limit,
            ),
            // USSD Limits

            max_Single_USSD_Transaction_Limit: formatAmount(data.max_Single_USSD_Transaction_Limit),
            max_Daily_USSD_Transaction_Limit: formatAmount(data.max_Daily_USSD_Transaction_Limit),
            specialLimit_Max_Single_USSD_Transaction_Limit: formatAmount(
              data.specialLimit_Max_Single_USSD_Transaction_Limit,
            ),
            specialLimit_Max_Daily_USSD_Transaction_Limit: formatAmount(
              data.specialLimit_Max_Daily_USSD_Transaction_Limit,
            ),

          }
          this.showSpinner = false
        }
      } catch (error) {
        // this.handleError(error)
      } finally {
        this.showSpinner = false
      }
    },

    async saveUssdTransactionLimit() {
      if (
        this.ussdLimitFormData.user_Single_Transaction_Limit > this.ussdLimitFormData.user_Daily_Transaction_Limit
      ) {
        this.alertError({ text: this.$t('Single Transaction Limit cannot be greater than Daily Transaction Limit') })
      } else {
        this.$refs.ussdLimitRules.validate().then(success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('this USSD Limit?')
            this.confirm(this.ussdLimitAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
          }
        })
      }
    },
    async ussdLimitAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          phoneNumber: this.casaBalanceFormData.phoneNumber,
          user_Single_Transaction_Limit: toKobo(this.ussdLimitFormData.user_Single_Transaction_Limit),
          user_Daily_Transaction_Limit: toKobo(this.ussdLimitFormData.user_Daily_Transaction_Limit),
          hasSpecialConsideration: this.ussdLimitFormData.hasSpecialConsideration,
        }
        const response = await this.addOrUpdate({ formData, permission: eChannelsPermission.UPDATE_CUSTOMER_USSD_LIMIT })
        const { status, data } = response
        if (status === 200 && data) {
          this.isLoading = false
          this.isDisabled = false
          this.alertSuccess({ text: this.$t('Your USSD Limit request was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.hideModal('ussd-limit-modal')
      }
    },
    async fetchUssdLimits() {
      try {
        this.showSpinner = true
        const response = await this.getUssdLimitDetails({
          phoneNumber: this.casaBalanceFormData.phoneNumber,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })
        const { status, data } = await response
        if (status === 200 && data.user_Daily_Transaction_Limit > 0 && data.user_Daily_Transaction_Limit > 0) {
          this.ussdLimitFormData = {
            user_Daily_Transaction_Limit: formatAmount(data.user_Daily_Transaction_Limit),
            user_Single_Transaction_Limit: formatAmount(data.user_Single_Transaction_Limit),
            hasSpecialConsideration: data.hasSpecialConsideration,
          }
          this.buttonLabel = this.$t('Update')
        } else {
          const res = await this.getInstitutionLimitDetails({
            permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
          })
          this.ussdLimitFormData = {
            user_Daily_Transaction_Limit: formatAmount(res.data.default_Max_Daily_USSD_Transaction_Limit),
            user_Single_Transaction_Limit: formatAmount(res.data.default_Max_Single_USSD_Transaction_Limit),
            hasSpecialConsideration: data.hasSpecialConsideration,
          }
          this.buttonLabel = this.$t('Update')
        }
      } catch (error) {
        this.handleError({ error })
      } finally {
        this.showSpinner = false
      }
    },

    async saveCustomerAccountLimit() {
      if (
        this.accountLimitFormData.user_Single_Transaction_Limit > this.accountLimitFormData.user_Daily_Transaction_Limit
      ) {
        this.alertError({ text: this.$t('Single Transaction Limit cannot be greater than Daily Transaction Limit') })
      } else {
        this.$refs.accountLimitRules.validate().then(success => {
          if (success) {
            const text1 = this.$t('Are you sure you want to')
            const text2 = this.$t('this Account Limit?')
            this.confirm(this.accountLimitAction, { text: `${text1} ${this.buttonLabel} ${text2}` })
          }
        })
      }
    },
    async accountLimitAction() {
      try {
        this.isLoading = true
        this.isDisabled = true
        const formData = {
          accountNumber: this.casaBalanceFormData.accountNumber,
          user_Single_Transaction_Limit: toKobo(this.accountLimitFormData.user_Single_Transaction_Limit),
          user_Daily_Transaction_Limit: toKobo(this.accountLimitFormData.user_Daily_Transaction_Limit),
          hasSpecialConsideration: this.accountLimitFormData.hasSpecialConsideration,
        }
        const response = this.buttonLabel === 'Save'
          ? await this.createAccountLimit({ formData, permission: eChannelsPermission.CREATE_ACCOUNT_LIMIT })
          : await this.updateAccountLimit({ formData, permission: eChannelsPermission.UPDATE_ACCOUNT_LIMIT })
        const { status, data } = response
        if (status === 200 && data) {
          this.isLoading = false
          this.isDisabled = false
          this.alertSuccess({ text: this.$t('Your Account Limit request was successful') })
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isLoading = false
        this.isDisabled = false
        this.hideModal('account-limit-modal')
      }
    },
    async fetchProducts() {
      try {
        const { status, data } = await this.getProducts({
          search: '',
          page: 0,
          limit: 0,
          permission: accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT,
        })

        if (status === 200 && data.data) {
          this.productTypeOptions = data.data.map(item => ({ label: item.name, value: item.code.toString() }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    customerAccountStatus(items) {
      this.reasonFormData = {
        items,
      }
      this.$root.$emit('bv::show::modal', 'activate-deactivate-reason-modal')
    },
    async manageCustomerAccountStatus() {
      const { accountNumber, customerName, accountType } = this.reasonFormData.items
      const actionType = this.reasonFormData.items.accountStatus === 'Active' ? 'Inactive' : 'Active'
      const action = this.reasonFormData.items.accountStatus === 'Active' ? this.$t('Deactivate') : this.$t('Activate')
      const permission = this.reasonFormData.items.accountStatus === 'Active'
        ? accountPermissions.DEACTIVATE_SAVING_CURRENT_ACCOUNT
        : accountPermissions.ACTIVATE_SAVING_CURRENT_ACCOUNT

      const updateStatus = async () => {
        const textA = this.$t('Your request to')
        const textB = this.$t('account with')
        const textC = this.$t('was successful')
        const textD = this.$t('Customer Account failed. Reason:')
        try {
          this.showSpinner = true
          const statusValues = {
            accountNumber,
            name: customerName,
            reason: this.reasonFormData.reason,
          }
          const response = await this.updateCustomerAccountStatus({
            statusValues,
            actionType,
            routeName: 'CustomerAccount',
            permission,
          })
          if (response.status === 200) {
            this.fetchCustomerAccount()
            this.alertSuccess({
              text: `${textA} ${action} ${this.$t(accountType)}'s ${textB} ${accountNumber} ${textC}`,
            })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({ text: `${textA} ${action} ${textD} ${error}` })
        } finally {
          this.showSpinner = false
        }
      }
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('this Customer Account?')
      this.confirm(updateStatus, { title: '', text: `${text1} ${action} ${text2}` })
    },
    handleAccountStatus(formData, route) {
      this.reasonFormData = {
        formData,
        route,
      }
      this.$root.$emit('bv::show::modal', 'add-reason-modal')
    },
    async manageAccountStatus() {
      const { formData, route, reason } = this.reasonFormData
      const isFreeze = route === 'Freeze' || route === 'Unfreeze' ? '' : 'on'
      const getResponse = mess => {
        const message = {
          PlacePND: 'Place PND',
          PlacePNC: 'Place PNC',
          Freeze: 'Freeze',
          Unfreeze: 'UnFreeze',
          RemovePND: 'Remove PND',
          RemovePNC: 'Remove PNC',
        }
        return message[mess]
      }

      let permission
      if (route === 'PlacePND') {
        permission = accountPermissions.PLACE_PND
      } else if (route === 'RemovePND') {
        permission = accountPermissions.UNPLACE_PND
      } else if (route === 'PlacePNC') {
        permission = accountPermissions.PLACE_PNC
      } else if (route === 'RemovePNC') {
        permission = accountPermissions.UNPLACE_PNC
      } else if (route === 'Freeze') {
        permission = accountPermissions.FREEZE_ACCOUNT
      } else if (route === 'Unfreeze') {
        permission = accountPermissions.UNFREEZE_ACCOUNT
      } else {
        permission = accountPermissions.VIEW_SAVING_CURRENT_ACCOUNT
      }
      const updateStatus = async () => {
        const textA = this.$t('Your request to')
        const textB = this.$t('account with')
        const textC = this.$t('was successful')
        const textD = this.$t('Customer Account failed. Reason:')
        try {
          this.showSpinner = true
          const response = await this.toggleCustomerAccountStatus({
            permission,
            name: formData.accountName,
            status: formData.accountStatus,
            accountNumber: formData.accountNumber,
            reason,
            initiatedBy: this.userDetails.email,
            route,
          })
          if (response.status === 200) {
            this.fetchCustomerAccount()
            this.alertSuccess({
              text: `${textA} ${getResponse(route)} ${isFreeze} ${formData.accountName}'s ${textB} ${
                formData.accountNumber
              } ${textC}`,
            })
            this.showSpinner = false
          }
        } catch (error) {
          this.alertError({ text: `${textA} ${route} ${textD} ${this.readError(error)}` })
        } finally {
          this.showSpinner = false
        }
      }
      const text1 = this.$t('Are you sure you want to')
      const text2 = this.$t('this Customer Account?')
      this.confirm(updateStatus, { title: '', text: `${text1} ${getResponse(route)} ${isFreeze} ${text2}` })
    },
    resetFilters() {
      this.filter = {}

      this.fetchCustomerAccount()
    },
    resetFilter() {
      this.filter = { ...null }

      this.fetchCustomerAccount()
    },
    applyFilters() {
      this.fetchCustomerAccount()
    },
    resetAvatarModal() {
      this.avatarLg = ''
      this.avatarModalTitle = ''
    },
    resetReasonForm() {
      this.reasonFormData = { ...null }
    },
  },
}
</script>

<style>
.custom-scroll {
  max-height: 270px;
  overflow-y: auto;
}
.custom-scroll::-webkit-scrollbar {
  width: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb {
  background-color: #aaa;
  border-radius: 4px;
}

.custom-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #888;
}
</style>
<style lang="scss" scoped>
.table-responsive {
  padding-bottom: 170px !important;
  margin-bottom: 0.5rem;
}
.per-page-selector {
  width: 90px;
}

.invoice-filter-select {
  min-width: 190px;

  ::v-deep .vs__selected-options {
    flex-wrap: nowrap;
  }

  ::v-deep .vs__selected {
    width: 100px;
  }
}
.upload-image {
  border: 2px dashed #dcd9d9;
  position: relative;
  width: 250px;
  height: 250px;
}
.format {
  color: #999999 !important;
}
form input[type='file'] {
  position: absolute;
  margin: 0;
  padding: 0;
  width: 100%;
  height: 100%;
  outline: none;
  opacity: 0;
}
.upload-instruction {
  padding-top: 22%;
  color: #000;
  text-align: center;
}
.view-details {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 1;
  background: #fff;
  border-radius: 50%;
  width: 40px;
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 10px;
  margin-left: 10px;
  cursor: pointer;
}
.upload {
  max-width: 100%;
  max-height: 100%;
}
.button-center {
  display: flex;
  justify-content: center;
  align-items: center;
}
</style>

<style lang="scss" scoped>
@import '../../../../../@core/styles/global.css';
@import '@core/scss/vue/libs/vue-select.scss';
</style>
